/**
 * SG AGM with Audited Financial Statements — 변수 ↔ 문서 매핑 (7 documents).
 */
export type AgmAfsDocKind =
  | 'attendance'
  | 'consent'
  | 'directors_resolution'
  | 'minutes_multiple'
  | 'minutes_sole_corporate'
  | 'minutes_sole_individual'
  | 'notice';

export const ALL_DOC_KINDS: AgmAfsDocKind[] = [
  'attendance',
  'consent',
  'directors_resolution',
  'minutes_multiple',
  'minutes_sole_corporate',
  'minutes_sole_individual',
  'notice',
];

export function classifyAgmAfsDoc(templateName: string): AgmAfsDocKind {
  const n = (templateName || '').toLowerCase();
  if (/minutes.*sole\s*individual/i.test(n)) return 'minutes_sole_individual';
  if (/minutes.*representative|minutes.*sole\s*corporate|holding\s*company/i.test(n)) {
    return 'minutes_sole_corporate';
  }
  if (/minutes.*multiple/i.test(n)) return 'minutes_multiple';
  if (/177|203|agreement.*section|consent.*short|short\s*notice/i.test(n)) return 'consent';
  if (/directors?['_\s]*resolutions?/i.test(n)) return 'directors_resolution';
  if (/notice.*agm|agm.*notice/i.test(n)) return 'notice';
  if (/attendance/i.test(n)) return 'attendance';
  return 'notice';
}

export const DOC_VARIABLES_BY_KIND: Record<AgmAfsDocKind, string[]> = {
  attendance: [
    'company_name',
    'company_registration_no',
    'agm_venue',
    'agm_date',
    'agm_time',
    'is_multiple_members',
    'member.name',
    'is_sole_corporate_member',
    'member_name',
    'member_representative_name',
    'is_sole_individual_member',
    'Signers',
  ],
  consent: [
    'company_name',
    'company_registration_no',
    'registered_office_address',
    'is_multiple_members',
    'is_sole_corporate_member',
    'member_representative_name',
    'member_name',
    'is_sole_individual_member',
    'agm_venue',
    'agm_date',
    'agm_time',
    'consent_date',
    'Signers',
  ],
  directors_resolution: [
    'company_name',
    'company_registration_no',
    'financial_year_end_date',
    'director_count',
    'specify_signers',
    'designated_signers',
    'signer_name',
    'has_directors_remuneration',
    'agm_date',
    'agm_time',
    'agm_venue',
    'is_short_notice',
    'annual_return_year',
    'resolution_date',
    'Signers',
  ],
  minutes_multiple: [
    'company_name',
    'company_registration_no',
    'agm_date',
    'agm_time',
    'agm_venue',
    'chairman_name',
    'financial_year_end_date',
    'has_directors_remuneration',
    'has_retiring_directors',
    'auditor_name',
    'Signers',
  ],
  minutes_sole_corporate: [
    'company_name',
    'company_registration_no',
    'member_representative_name',
    'member_name',
    'agm_date',
    'financial_year_end_date',
    'has_directors_remuneration',
    'has_retiring_directors',
    'auditor_name',
    'Signers',
  ],
  minutes_sole_individual: [
    'company_name',
    'company_registration_no',
    'member_name',
    'agm_date',
    'financial_year_end_date',
    'has_directors_remuneration',
    'has_retiring_directors',
    'auditor_name',
    'Signers',
  ],
  notice: [
    'company_name',
    'company_registration_no',
    'registered_office_address',
    'agm_venue',
    'agm_date',
    'agm_time',
    'financial_year_end_date',
    'has_retiring_directors',
    'auditor_name',
    'has_directors_remuneration',
    'notice_date',
    'Signers',
  ],
};

export const VARIABLE_DOC_KINDS: Record<string, AgmAfsDocKind[]> = (() => {
  const map: Record<string, Set<AgmAfsDocKind>> = {};
  for (const kind of ALL_DOC_KINDS) {
    for (const key of DOC_VARIABLES_BY_KIND[kind]) {
      if (!map[key]) map[key] = new Set();
      map[key].add(kind);
    }
  }
  const out: Record<string, AgmAfsDocKind[]> = {};
  for (const [key, set] of Object.entries(map)) {
    out[key] = [...set];
  }
  return out;
})();

export function docKindsForVariable(varKey: string): AgmAfsDocKind[] {
  return VARIABLE_DOC_KINDS[varKey] ?? [];
}

export function docNamesForVariable(
  varKey: string,
  docs: Array<{ name: string; kind: AgmAfsDocKind }>,
): string[] {
  const kinds = docKindsForVariable(varKey);
  return docs.filter((d) => kinds.includes(d.kind)).map((d) => d.name);
}

export function docNamesForKinds(
  kinds: AgmAfsDocKind[],
  docs: Array<{ name: string; kind: AgmAfsDocKind }>,
): string[] {
  return docs.filter((d) => kinds.includes(d.kind)).map((d) => d.name);
}
